import { ChangeDetectionStrategy, Component, output, signal } from '@angular/core';
import { PmsSetup } from '../../../../../types/pms';
import { OperaCloudSsdConnectComponent } from './ssd/ssd.component';
import { OperaCloudOcimConnectComponent } from './ocim/ocim.component';

@Component({
    selector: 'juyo-opera-cloud-connect',
    styleUrl: './opera-cloud.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [OperaCloudSsdConnectComponent, OperaCloudOcimConnectComponent],
    template: `
        @switch (selectedPath()) {
            @case ('SSD') {
                <juyo-opera-cloud-ssd-connect
                    (back)="selectedPath.set(null)"
                    (valueChange)="valueChange.emit($event)" />
            }
            @case ('OCIM') {
                <juyo-opera-cloud-ocim-connect
                    (back)="selectedPath.set(null)"
                    (valueChange)="valueChange.emit($event)" />
            }
            @default {
                <h3 class="step-title">Connect to Opera Cloud</h3>
                <p class="step-subtitle">Opera Cloud has two setup paths depending on your environment. Choose the one that matches your tenant. Setup must be completed by an Opera Administrator.</p>

                <div class="tiles">
                    <button type="button" class="tile" (click)="selectedPath.set('SSD')">
                        <div class="tile-header">
                            <span class="tile-name">SSD</span>
                            <span class="badge badge--legacy">LEGACY</span>
                        </div>
                        <p class="tile-description">You're on SSD if your login URL contains:</p>
                        <div class="url-snippet">ssd-ohs.oracleindustry.com</div>
                        <p class="tile-estimate">
                            <span class="material-symbols-outlined">schedule</span>
                            ~30 min of admin work
                        </p>
                    </button>

                    <button type="button" class="tile" (click)="selectedPath.set('OCIM')">
                        <div class="tile-header">
                            <span class="tile-name">OCIM</span>
                            <span class="badge badge--standard">CURRENT STANDARD</span>
                        </div>
                        <p class="tile-description">You're on OCIM if your login URL contains:</p>
                        <div class="url-snippet">oraclehospitality.&lt;region&gt;.ocs.oraclecloud.com</div>
                        <p class="tile-estimate">
                            <span class="material-symbols-outlined">schedule</span>
                            ~15 min of admin work
                        </p>
                    </button>
                </div>

                <div class="warning-box">
                    <span class="warning-label">Important:</span> Choose only one path. SSD and OCIM are mutually exclusive — do not complete both. Connection steps must be performed by an OPERA Administrator.
                </div>

                <button type="button" class="help-toggle" (click)="showHelp.set(!showHelp())"
                        [attr.aria-expanded]="showHelp()" aria-controls="help-panel">
                    <span class="material-symbols-outlined">help</span>
                    Still not sure?
                </button>

                @if (showHelp()) {
                    <div class="help-panel" id="help-panel">
                        <p class="help-title">Confirm which one you're on</p>
                        <ol class="help-steps">
                            <li>Open the browser tab you use for Opera Cloud and check the URL — the substring (<strong>ssd-ohs...</strong> vs <strong>oraclehospitality...</strong>) tells you the environment.</li>
                            <li>Or, in the Developer Portal, go to <strong>Environments &rarr; View Details &rarr; Auth Scheme</strong> — it will say <strong>SSD</strong> or <strong>OCIM</strong> directly.</li>
                        </ol>
                        <p class="help-footer">If you're still unsure, ask your <strong>OPERA Administrator</strong>.</p>
                    </div>
                }
            }
        }
    `,
})
export class OperaCloudConnectComponent {
    public valueChange = output<PmsSetup | null>();

    protected selectedPath = signal<'SSD' | 'OCIM' | null>(null);
    protected showHelp = signal(false);
}
